import React, { useState, useEffect } from 'react';
import { bookingAPI, paymentAPI } from '../services/api';
import { processRazorpayPayment } from '../services/razorpay';
import ReviewForm from '../components/ReviewForm';
import './MyBookings.css';

const MyBookings = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [updatingStatus, setUpdatingStatus] = useState({});
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [processingPayment, setProcessingPayment] = useState({});

  const statusOptions = [
    { value: 'all', label: 'All Bookings' },
    { value: 'pending', label: 'Pending' },
    { value: 'accepted', label: 'Accepted' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' }
  ];

  const serviceIcons = {
    'sweeping-mopping': '🧹',
    'cooking': '👨‍🍳',
    'laundry': '👕',
    'gardening': '🌱',
    'pet-care': '🐕',
    'elderly-care': '👴',
    'babysitting': '👶',
    'hair-dresser': '✂️',
    'other': '🔧'
  };

  useEffect(() => {
    loadBookings();
  }, []);

  const loadBookings = async () => {
    try {
      setLoading(true);
      const response = await bookingAPI.getMyBookings();
      setBookings(response.data.bookings);
    } catch (err) {
      setError('Failed to load bookings. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (bookingId, newStatus) => {
    try {
      setUpdatingStatus(prev => ({ ...prev, [bookingId]: true }));
      
      await bookingAPI.updateBookingStatus(bookingId, newStatus);
      
      // Update the booking in the local state
      setBookings(prev => 
        prev.map(booking => 
          booking._id === bookingId 
            ? { ...booking, status: newStatus }
            : booking
        )
      );

      // Show success message
      const statusMessages = {
        'accepted': 'Booking accepted successfully!',
        'completed': 'Booking marked as completed!',
        'cancelled': 'Booking cancelled successfully!'
      };
      
      alert(statusMessages[newStatus] || 'Status updated successfully!');
      
    } catch (err) {
      setError(err.message || 'Failed to update booking status.');
    } finally {
      setUpdatingStatus(prev => ({ ...prev, [bookingId]: false }));
    }
  };

  const handlePayment = async (booking) => {
    try {
      setProcessingPayment(prev => ({ ...prev, [booking._id]: true }));
      
      const paymentResult = await processRazorpayPayment(booking._id, {
        customerName: booking.customerName || 'Customer',
        customerEmail: '',
        customerPhone: ''
      });

      if (paymentResult.success) {
        // Update the booking in state to reflect payment completion
        setBookings(prev => 
          prev.map(b => 
            b._id === booking._id 
              ? { ...b, payment: { ...b.payment, status: 'completed' } }
              : b
          )
        );
        setError(''); // Clear any previous errors
        alert('Payment completed successfully! Your booking is confirmed.');
        // Refresh bookings to update payment status
        loadBookings();
      }
    } catch (error) {
      setError(`Payment failed: ${error.message}. Please try again.`);
    } finally {
      setProcessingPayment(prev => ({ ...prev, [booking._id]: false }));
    }
  };

  const getStatusBadge = (status) => {
    const statusStyles = {
      'pending': { bg: '#fff3cd', color: '#856404', icon: '⏳' },
      'accepted': { bg: '#d1ecf1', color: '#0c5460', icon: '✅' },
      'completed': { bg: '#d4edda', color: '#155724', icon: '🎉' },
      'cancelled': { bg: '#f8d7da', color: '#721c24', icon: '❌' }
    };

    const style = statusStyles[status] || statusStyles.pending;
    
    return (
      <span 
        className="status-badge"
        style={{ 
          backgroundColor: style.bg, 
          color: style.color 
        }}
      >
        {style.icon} {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getUserRole = () => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    return user.role || 'customer';
  };

  const handleReviewSubmitted = () => {
    setShowReviewForm(false);
    setSelectedBooking(null);
    // Optionally reload bookings to update any review-related status
    loadBookings();
  };

  const handleCancelReview = () => {
    setShowReviewForm(false);
    setSelectedBooking(null);
  };

  const filteredBookings = statusFilter === 'all' 
    ? bookings 
    : bookings.filter(booking => booking.status === statusFilter);

  const canUpdateStatus = (booking, newStatus) => {
    const userRole = getUserRole();
    
    if (newStatus === 'accepted' || newStatus === 'completed') {
      return userRole === 'helper' && booking.status === 'pending';
    }
    
    if (newStatus === 'cancelled') {
      return booking.status === 'pending' || booking.status === 'accepted';
    }
    
    return false;
  };

  const formatTimeRange = (startTime, endTime) => {
    if (!startTime || !endTime) return 'Time not set';
    
    const formatTime = (time) => {
      const [hour, minute] = time.split(':').map(Number);
      const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
      const ampm = hour >= 12 ? 'PM' : 'AM';
      return `${displayHour}:${minute.toString().padStart(2, '0')} ${ampm}`;
    };
    
    return `${formatTime(startTime)} - ${formatTime(endTime)}`;
  };

  const calculateDuration = (startTime, endTime) => {
    if (!startTime || !endTime) return 0;
    const [startHour, startMin] = startTime.split(':').map(Number);
    const [endHour, endMin] = endTime.split(':').map(Number);
    const startMinutes = startHour * 60 + startMin;
    const endMinutes = endHour * 60 + endMin;
    return (endMinutes - startMinutes) / 60;
  };

  const renderActionButtons = (booking) => {
    const userRole = getUserRole();
    
    const buttons = [];
    
    // Helper actions
    if (userRole === 'helper') {
      if (booking.status === 'pending') {
        buttons.push(
          <button 
            key="accept"
            className="action-button accept"
            onClick={() => handleStatusUpdate(booking._id, 'accepted')}
            disabled={updatingStatus[booking._id]}
          >
            {updatingStatus[booking._id] ? 'Accepting...' : 'Accept'}
          </button>
        );
        
        buttons.push(
          <button 
            key="decline"
            className="action-button decline"
            onClick={() => handleStatusUpdate(booking._id, 'cancelled')}
            disabled={updatingStatus[booking._id]}
          >
            {updatingStatus[booking._id] ? 'Declining...' : 'Decline'}
          </button>
        );
      }
      
      if (booking.status === 'accepted') {
        buttons.push(
          <button 
            key="complete"
            className="action-button complete"
            onClick={() => handleStatusUpdate(booking._id, 'completed')}
            disabled={updatingStatus[booking._id]}
          >
            {updatingStatus[booking._id] ? 'Completing...' : 'Mark Complete'}
          </button>
        );
      }
    }
    
    // Customer actions
    if (userRole === 'customer') {
      // Show payment button for accepted bookings with online payment method that haven't been paid
      if (booking.status === 'accepted' && 
          booking.payment && 
          booking.payment.method === 'online' && 
          booking.payment.status === 'pending') {
        buttons.push(
          <button 
            key="pay"
            className="action-button pay"
            onClick={() => handlePayment(booking)}
            disabled={processingPayment[booking._id]}
          >
            {processingPayment[booking._id] ? 'Processing...' : '💳 Make Payment'}
          </button>
        );
      }
      
      // Show payment completed badge if payment is done
      if (booking.status === 'accepted' && 
          booking.payment && 
          booking.payment.method === 'online' && 
          booking.payment.status === 'completed') {
        buttons.push(
          <div key="paid" className="payment-success-badge">
            ✅ Payment Completed
          </div>
        );
      }
      
      // Cancel button for pending or accepted bookings WITH PAYMENT NOT COMPLETED
      if ((booking.status === 'pending' || 
           (booking.status === 'accepted' && 
            !(booking.payment && booking.payment.method === 'online' && booking.payment.status === 'completed')))) {
        buttons.push(
          <button 
            key="cancel"
            className="action-button cancel"
            onClick={() => handleStatusUpdate(booking._id, 'cancelled')}
            disabled={updatingStatus[booking._id]}
          >
            {updatingStatus[booking._id] ? 'Cancelling...' : '❌ Cancel'}
          </button>
        );
      }
      
      // Review button for completed bookings
      if (booking.status === 'completed') {
        buttons.push(
          <button 
            key="review"
            className="action-button review"
            onClick={() => {
              setSelectedBooking(booking);
              setShowReviewForm(true);
            }}
          >
            ⭐ Add Review
          </button>
        );
      }
    }
    
    return buttons.length > 0 ? (
      <div className="booking-actions">
        {buttons}
      </div>
    ) : null;
  };

  if (loading) {
    return (
      <div className="bookings-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading your bookings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bookings-container">
      <div className="bookings-header">
        <h1>📋 My Bookings</h1>
        <p>Manage and track all your service bookings</p>
      </div>

      {error && <div className="error-message">{error}</div>}

      {/* Filter Section */}
      <div className="filter-section">
        <label htmlFor="statusFilter">Filter by Status:</label>
        <select
          id="statusFilter"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="filter-select"
        >
          {statusOptions.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <span className="booking-count">
          {filteredBookings.length} booking{filteredBookings.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Bookings List */}
      {filteredBookings.length === 0 ? (
        <div className="no-bookings">
          <div className="no-bookings-icon">📋</div>
          <h3>No bookings found</h3>
          <p>
            {statusFilter === 'all' 
              ? getUserRole() === 'helper' 
                ? "You don't have any job assignments yet."
                : "You haven't made any bookings yet. Start by browsing available helpers!"
              : `No ${statusFilter} bookings found. Try changing the filter.`
            }
          </p>
          {getUserRole() === 'customer' && (
            <button 
              className="browse-btn"
              onClick={() => window.location.href = '/helpers'}
            >
              Browse Helpers
            </button>
          )}
        </div>
      ) : (
        <div className="bookings-grid">
          {filteredBookings.map(booking => (
            <div key={booking._id} className="booking-card">
              <div className="booking-header">
                <div className="service-info">
                  <span className="service-icon">
                    {serviceIcons[booking.service]}
                  </span>
                  <h3>{booking.service.charAt(0).toUpperCase() + booking.service.slice(1).replace('-', ' ')}</h3>
                </div>
                {getStatusBadge(booking.status)}
              </div>

              <div className="booking-details">
                <div className="detail-row">
                  <span className="detail-label">📅 Date:</span>
                  <span className="detail-value">{formatDate(booking.date)}</span>
                </div>
                
                <div className="detail-row">
                  <span className="detail-label">🕒 Time:</span>
                  <span className="detail-value">{formatTimeRange(booking.startTime, booking.endTime)}</span>
                </div>
                
                <div className="detail-row">
                  <span className="detail-label">⏱️ Duration:</span>
                  <span className="detail-value">{calculateDuration(booking.startTime, booking.endTime)} hour{calculateDuration(booking.startTime, booking.endTime) > 1 ? 's' : ''}</span>
                </div>

                <div className="detail-row">
                  <span className="detail-label">
                    {getUserRole() === 'customer' ? '👨‍🔧 Helper:' : '👤 Customer:'}
                  </span>
                  <span className="detail-value">
                    {getUserRole() === 'customer' 
                      ? booking.helperId?.name || 'Helper'
                      : booking.customerId?.name || 'Customer'
                    }
                  </span>
                </div>

                <div className="detail-row">
                  <span className="detail-label">📍 Address:</span>
                  <span className="detail-value">{booking.customerAddress}</span>
                </div>

                <div className="detail-row">
                  <span className="detail-label">💰 Amount:</span>
                  <span className="detail-value amount">₹{booking.pricing?.totalAmount || booking.totalAmount || 'N/A'}</span>
                </div>

                {booking.specialRequests && (
                  <div className="detail-row">
                    <span className="detail-label">📝 Special Requests:</span>
                    <span className="detail-value">{booking.specialRequests}</span>
                  </div>
                )}
              </div>

              {renderActionButtons(booking)}

              <div className="booking-footer">
                <small>Booking ID: {booking._id.slice(-6).toUpperCase()}</small>
                <small>Created: {new Date(booking.createdAt).toLocaleDateString()}</small>
              </div>
            </div>
          ))}
        </div>
      )}

      {showReviewForm && selectedBooking && (
        <ReviewForm
          booking={selectedBooking}
          onReviewSubmitted={handleReviewSubmitted}
          onCancel={handleCancelReview}
        />
      )}
    </div>
  );
};

export default MyBookings; 